import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import api from "../../lib/api";
import { Card, Badge } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import { formatPrice } from "../../lib/utils";

const EMPTY_FORM = {
  service_id: "",
  name: "",
  description: "",
  session_count: 5,
  price: "",
  validity_days: 365,
};

export default function Packages() {
  const [packages, setPackages] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = () =>
    api
      .get("/owner/packages")
      .then(({ data }) => setPackages(data))
      .catch(() => setPackages([]))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    api.get("/owner/services").then(({ data }) => setServices(data)).catch(() => setServices([]));
  }, []);

  const startCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, service_id: services[0]?.id || "" });
    setShowForm(true);
  };

  const startEdit = (pkg) => {
    setEditingId(pkg.id);
    setForm({
      service_id: pkg.service_id,
      name: pkg.name,
      description: pkg.description || "",
      session_count: pkg.session_count,
      price: pkg.price,
      validity_days: pkg.validity_days,
    });
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        const { service_id, ...editable } = form;
        await api.put(`/owner/packages/${editingId}`, editable);
      } else {
        await api.post("/owner/packages", form);
      }
      cancelForm();
      load();
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (id) => {
    await api.delete(`/owner/packages/${id}`);
    load();
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-montserrat text-2xl font-bold text-slate-900">Packages</h1>
        <Button size="sm" onClick={() => (showForm ? cancelForm() : startCreate())}>
          <Plus className="h-4 w-4" /> Add package
        </Button>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        Bundle prepaid sessions of a service at a discount to encourage repeat bookings. Customers buy
        the package once, then redeem one session per booking.
      </p>

      {showForm && (
        <Card className="mb-6 p-4">
          <p className="mb-3 text-sm font-semibold text-slate-900">{editingId ? "Edit package" : "New package"}</p>
          <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-700">Service</label>
              <select
                required
                disabled={!!editingId}
                value={form.service_id}
                onChange={(e) => setForm({ ...form, service_id: e.target.value })}
                className="h-11 w-full rounded-xl border border-input bg-white px-4 text-sm outline-none focus:border-primary disabled:opacity-60"
              >
                <option value="" disabled>Select a service</option>
                {services.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <Input placeholder="Package name (e.g. 5-visit pack)" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input placeholder="Sessions included" type="number" min={2} required value={form.session_count} onChange={(e) => setForm({ ...form, session_count: e.target.value })} />
            <Input placeholder="Package price" type="number" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            <Input placeholder="Valid for (days)" type="number" value={form.validity_days} onChange={(e) => setForm({ ...form, validity_days: e.target.value })} />
            <Input
              placeholder="Description (optional)"
              className="sm:col-span-2"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <div className="flex gap-2 sm:col-span-2">
              <Button type="submit" disabled={saving}>{saving ? "Saving…" : editingId ? "Save changes" : "Save package"}</Button>
              <Button type="button" variant="outline" onClick={cancelForm}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : packages.length === 0 ? (
        <p className="text-muted-foreground">No packages yet — bundle a service above.</p>
      ) : (
        <div className="divide-y divide-border rounded-2xl border border-border">
          {packages.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div>
                <p className="font-medium text-slate-900">{p.name}</p>
                <p className="text-xs text-muted-foreground">
                  {p.session_count} × {p.service?.name} · valid {p.validity_days} days
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-semibold text-slate-900">{formatPrice(p.price)}</span>
                <Badge variant={p.is_active ? "accent" : "default"}>{p.is_active ? "Active" : "Inactive"}</Badge>
                <button onClick={() => startEdit(p)} className="text-muted-foreground hover:text-primary">
                  <Pencil className="h-4 w-4" />
                </button>
                {p.is_active && (
                  <button onClick={() => deactivate(p.id)} className="text-muted-foreground hover:text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
